import type { TeamMemberGroupType } from "./team-member-group";

export const RECORD_SCHEDULE_STATUS_VALUES = ["active", "paused", "archived"] as const;
export type RecordScheduleStatus = (typeof RECORD_SCHEDULE_STATUS_VALUES)[number];

export const RECORD_SCHEDULE_ASSIGNMENT_MODE_VALUES = ["jobs", "team_members"] as const;
export type RecordScheduleAssignmentMode = (typeof RECORD_SCHEDULE_ASSIGNMENT_MODE_VALUES)[number];

export const RECORD_SCHEDULE_TARGET_ASSIGNMENT_MODE_VALUES = ["job", "team_member"] as const;
export type RecordScheduleTargetAssignmentMode =
  (typeof RECORD_SCHEDULE_TARGET_ASSIGNMENT_MODE_VALUES)[number];

/** Jobs-mode only: one shared record anyone eligible may complete, or one per eligible person. */
export const RECORD_SCHEDULE_COMPLETION_MODE_VALUES = ["shared", "per_eligible"] as const;
export type RecordScheduleCompletionMode = (typeof RECORD_SCHEDULE_COMPLETION_MODE_VALUES)[number];

export const RECORD_SCHEDULE_PARENT_TYPE_VALUES = ["safety_meeting", "safety_checklist"] as const;
export type RecordScheduleParentType = (typeof RECORD_SCHEDULE_PARENT_TYPE_VALUES)[number];

export const RECORD_SCHEDULE_JOB_ROLE_GROUP_TYPE_VALUES = [
  "job_supervisor",
  "job_superintendent",
  "job_project_manager",
] as const satisfies readonly TeamMemberGroupType[];
export type RecordScheduleJobRoleGroupType = (typeof RECORD_SCHEDULE_JOB_ROLE_GROUP_TYPE_VALUES)[number];

// Fallback for jobs-mode schedules with no role groups; keep preview and backend resolution aligned.
export const RECORD_SCHEDULE_DEFAULT_JOB_ROLE_GROUP_TYPES = [
  "job_supervisor",
  "job_superintendent",
] as const satisfies readonly RecordScheduleJobRoleGroupType[];

export const RECORD_SCHEDULE_OCCURRENCE_OUTCOME_VALUES = [
  "completed_on_time",
  "completed_late",
  "missed",
  "admin_archived",
] as const;
export type RecordScheduleOccurrenceOutcome = (typeof RECORD_SCHEDULE_OCCURRENCE_OUTCOME_VALUES)[number];

export const RECORD_SCHEDULE_OCCURRENCE_STATUS_VALUES = [
  "scheduled",
  "overdue",
  "completed",
  "archived",
] as const;
export type RecordScheduleOccurrenceStatus = (typeof RECORD_SCHEDULE_OCCURRENCE_STATUS_VALUES)[number];

/** Minimum seconds before due_at when a consumer may be materialized (1 hour). */
export const RECORD_SCHEDULE_MIN_LEAD_TIME_SECONDS = 3600;

/** Default lead time: 1 day before due_at. */
export const RECORD_SCHEDULE_DEFAULT_LEAD_TIME_SECONDS = 86400;

/**
 * Occurrence statuses for materialized consumers that should not count as held meetings
 * (in-progress stubs, missed auto-archives, admin-archived stubs).
 */
export const RECORD_SCHEDULE_INCOMPLETE_CONSUMER_STATUS_VALUES =
  RECORD_SCHEDULE_OCCURRENCE_STATUS_VALUES.filter((status) => status !== "completed");
export type RecordScheduleIncompleteConsumerStatus =
  (typeof RECORD_SCHEDULE_INCOMPLETE_CONSUMER_STATUS_VALUES)[number];

/**
 * Denormalized link from a schedule consumer record back to the framework occurrence.
 */
export type RecordScheduleLink = {
  occurrence_id: string;
  status: RecordScheduleOccurrenceStatus;
  /** Snapshotted at materialization so inbox filters can query eligibility without re-resolving roles. */
  eligible_team_member_ids?: string[];
};

/**
 * Optional record schedule linkage on consumer models (SafetyMeeting, FormSubmission, …).
 * Present when the framework materializes a stub at generate time; absent on ad-hoc records.
 */
export type RecordSchedulable = {
  record_schedule?: RecordScheduleLink;
};
